/**
 * Layout bridge — DXF LAYOUT objects (OBJECTS section) <-> DrawingModel
 * `layouts` map. Kept in its own module for the same 2000-line guardrail
 * reason as dxfBridge.blockRecords.ts.
 *
 * Public surface:
 *   - importLayouts(objectsSection)          : Record<name, Layout>
 *   - exportLayouts(layouts)                 : DxfLayoutEntry[]
 *
 * AutoCAD pairs each paper-space BLOCK_RECORD with a LAYOUT object via
 * a two-way handle link:
 *   BLOCK_RECORD (group 340) → LAYOUT handle
 *   LAYOUT (group 330)       → BLOCK_RECORD handle
 * Only the BLOCK_RECORD side is required for importing layouts into the
 * drawing-model because the tab name is stored on the LAYOUT object.
 * The writer reconstitutes both sides from the imported DrawingDocument.
 *
 * # PDF-6.1 — full per-layout PLOTSETTINGS
 *
 * The model `Layout` carries `plotSettings: LayoutPlotSettings` as the
 * AUTHORITATIVE per-layout plot configuration. `convertRawLayout` builds it
 * from the parsed `DxfLayoutEntry` PLOTSETTINGS fields (group codes 74/44/45/
 * 40-43/46/47/48/49/140/141/142/143/70/72/73/1/7) and `exportLayouts` writes
 * it straight back, so the sub-object round-trips. The four legacy flat
 * fields (paperSize/plotPaperUnits/plotRotation/plotOrigin) are kept as a
 * DERIVED MIRROR of `plotSettings` for the scene/camera page-setup path
 * (`extractPageSetup`). On export `plotSettings` is authoritative — the flat
 * mirror only SEEDS it when `plotSettings` is still the canonical schema
 * default (legacy flat-only layouts); a default flat value never overrides a
 * populated `plotSettings` (Codex P2 #189 — see `effectivePlotSettings`).
 */
import type { DxfLayoutEntry, DxfObjectsSection } from "@dxf-ai-editor/dxf-io";

import {
  PlotAreaType,
  PlotPaperUnits,
  PlotRotation,
  defaultPlotSettings,
  type LayoutPlotSettings,
} from "./layoutPlotSettings";
import type { Layout } from "./model";

/** Map a raw numeric plot-area type (group 74) onto the enum (clamp 0..5). */
function toPlotAreaType(n: number | undefined): PlotAreaType {
  switch (n) {
    case 0:
      return PlotAreaType.Display;
    case 1:
      return PlotAreaType.Extents;
    case 2:
      return PlotAreaType.Limits;
    case 3:
      return PlotAreaType.View;
    case 4:
      return PlotAreaType.Window;
    case 5:
      return PlotAreaType.Layout;
    default:
      return PlotAreaType.Layout;
  }
}

/** Map a raw numeric plot-paper-units (group 72) onto the enum. */
function toPlotPaperUnits(n: number | undefined): PlotPaperUnits {
  switch (n) {
    case 0:
      return PlotPaperUnits.Inches;
    case 1:
      return PlotPaperUnits.Millimeters;
    case 2:
      return PlotPaperUnits.Pixels;
    default:
      return PlotPaperUnits.Millimeters;
  }
}

/** Map a raw numeric plot-rotation (group 73) onto the enum (clamp 0..3). */
function toPlotRotation(n: number | undefined): PlotRotation {
  switch (n) {
    case 1:
      return PlotRotation.Quarter;
    case 2:
      return PlotRotation.Half;
    case 3:
      return PlotRotation.ThreeQuarter;
    default:
      return PlotRotation.None;
  }
}

/**
 * Build a full {@link LayoutPlotSettings} from a parsed DXF LAYOUT entry. Any
 * field the DXF omitted falls back to the corresponding `defaultPlotSettings`
 * value, so a LAYOUT with a sparse / absent PLOTSETTINGS block still yields a
 * valid (A4) page configuration.
 */
export function rawLayoutToPlotSettings(
  raw: DxfLayoutEntry,
): LayoutPlotSettings {
  const d = defaultPlotSettings();
  return {
    pageSetupName: raw.pageSetupName ?? d.pageSetupName,
    paperName: raw.paperSize ?? d.paperName,
    paperWidthMm: raw.paperWidthMm ?? d.paperWidthMm,
    paperHeightMm: raw.paperHeightMm ?? d.paperHeightMm,
    units: toPlotPaperUnits(raw.plotPaperUnits),
    margins: raw.plotMargins
      ? { ...raw.plotMargins }
      : { ...d.margins },
    plotAreaType: toPlotAreaType(raw.plotAreaType),
    window: raw.plotWindow ? { ...raw.plotWindow } : { ...d.window },
    scale: raw.plotScale ? { ...raw.plotScale } : { ...d.scale },
    offset: raw.plotOrigin
      ? { x: raw.plotOrigin.x, y: raw.plotOrigin.y }
      : { ...d.offset },
    rotation: toPlotRotation(raw.plotRotation),
    plotStyleName: raw.plotStyleName ?? d.plotStyleName,
    flags: raw.plotLayoutFlags ?? d.flags,
  };
}

/**
 * Build a default {@link Layout} page-setup object for a from-scratch /
 * synthetic layout space that has no DXF LAYOUT object (PDF-6.1) — and for
 * runtime-created layouts (LAYOUT NEW / RENAME materialisation in the web
 * reducer). Seeds the canonical `defaultPlotSettings()` (A4 portrait, Layout
 * area, 1:1) AND keeps the four legacy FLAT mirror fields
 * (paperSize/plotPaperUnits/plotRotation/plotOrigin) in sync with it — the
 * mirrors are read by `effectivePlotSettings` (export) and `extractPageSetup`
 * (scene/camera page setup), so leaving them at the schema zero-defaults
 * (paperSize=""/plotPaperUnits=0=inches) diverges the exported group 72 and
 * the page-setup units from the authoritative A4/mm `plotSettings` (Codex
 * P2-2 PR #311). Single source of truth shared by the importer
 * (`buildImportedSpaces`) and the layout-lifecycle reducer
 * (`geometryReducerLayout.ts`).
 */
export function buildDefaultLayout(name: string, tabOrder: number): Layout {
  const plotSettings = defaultPlotSettings();
  return {
    name,
    handle: "",
    ownerBlockRecordHandle: "",
    tabOrder,
    plotSettings,
    paperSize: plotSettings.paperName,
    plotPaperUnits: plotSettings.units,
    plotRotation: plotSettings.rotation,
    plotOrigin: { x: plotSettings.offset.x, y: plotSettings.offset.y },
    minimumLimits: { x: 0, y: 0 },
    maximumLimits: { x: 420, y: 297 },
    insertionBase: { x: 0, y: 0, z: 0 },
    minimumExtents: { x: 0, y: 0, z: 0 },
    maximumExtents: { x: 0, y: 0, z: 0 },
    elevation: 0,
    orthographicViewType: 0,
  };
}

/** Convert a single raw DXF layout entry to a drawing-model Layout object. */
export function convertRawLayout(raw: DxfLayoutEntry): Layout {
  const plotSettings = rawLayoutToPlotSettings(raw);
  return {
    name: raw.name ?? "",
    handle: raw.handle ?? "",
    ownerBlockRecordHandle: raw.ownerHandle ?? "",
    // ROOT C — preserve the ACAD_LAYOUT dictionary owner (group 330 in the
    // AcDbPlotSettings context) so a re-export re-emits both 330s in order.
    ...(raw.dictionaryOwnerHandle
      ? { dictionaryOwnerHandle: raw.dictionaryOwnerHandle }
      : {}),
    // round-17 BUG 3 — preserve the HEADER-slot `102` reactor / xdictionary block
    // (`headerExtraGroups`, captured by dxf-io `parseLayoutEntry`) so a re-export
    // re-emits it in its original positional slot. Copy the `DxfGroup[]` into the
    // structurally-identical model-local `{ code, value }[]` (plain array copy).
    // Optional — omitted when the source LAYOUT carried no header-slot 102 block.
    ...(raw.headerExtraGroups && raw.headerExtraGroups.length > 0
      ? {
          headerExtraGroups: raw.headerExtraGroups.map((g) => ({
            code: g.code,
            value: g.value,
          })),
        }
      : {}),
    tabOrder: raw.tabOrder ?? 0,
    plotSettings,
    // Flat mirror — kept in sync with plotSettings for the scene page-setup
    // path (extractPageSetup) and the dxf-io transport shape.
    paperSize: plotSettings.paperName,
    plotPaperUnits: plotSettings.units,
    plotRotation: plotSettings.rotation,
    plotOrigin: { x: plotSettings.offset.x, y: plotSettings.offset.y },
    minimumLimits: raw.minimumLimits ?? { x: 0, y: 0 },
    maximumLimits: raw.maximumLimits ?? { x: 420, y: 297 },
    insertionBase: raw.insertionBase ?? { x: 0, y: 0, z: 0 },
    minimumExtents: raw.minimumExtents ?? { x: 0, y: 0, z: 0 },
    maximumExtents: raw.maximumExtents ?? { x: 0, y: 0, z: 0 },
    elevation: raw.elevation ?? 0,
    orthographicViewType: raw.orthographicViewType ?? 0,
  };
}

export function importLayouts(
  objectsSection: DxfObjectsSection | null,
): Record<string, Layout> {
  const out: Record<string, Layout> = {};
  if (!objectsSection) return out;
  for (const raw of objectsSection.layouts) {
    if (!raw.name) continue;
    const layout = convertRawLayout(raw);
    out[layout.name] = layout;
  }
  return out;
}

/**
 * Serialise the authoritative `plotSettings` back onto the DXF transport
 * fields of a {@link DxfLayoutEntry}. The writer (`serializeLayoutEntry`)
 * emits these as the AcDbPlotSettings group block. Round-trip identical with
 * `rawLayoutToPlotSettings`.
 */
function applyPlotSettingsToEntry(
  entry: DxfLayoutEntry,
  settings: LayoutPlotSettings,
): void {
  entry.pageSetupName = settings.pageSetupName;
  entry.paperSize = settings.paperName;
  entry.paperWidthMm = settings.paperWidthMm;
  entry.paperHeightMm = settings.paperHeightMm;
  entry.plotPaperUnits = settings.units;
  entry.plotMargins = { ...settings.margins };
  entry.plotAreaType = settings.plotAreaType;
  entry.plotOrigin = { x: settings.offset.x, y: settings.offset.y };
  entry.plotWindow = { ...settings.window };
  entry.plotScale = { ...settings.scale };
  entry.plotRotation = settings.rotation;
  entry.plotLayoutFlags = settings.flags;
  entry.plotStyleName = settings.plotStyleName;
}

/**
 * Whether a {@link LayoutPlotSettings} is byte-for-byte the canonical
 * {@link defaultPlotSettings} (A4 portrait, Layout area, 1:1, no rotation/
 * offset, PrintLineweights). Used by {@link effectivePlotSettings} to decide
 * whether `plotSettings` carries authoritative values or is merely the schema
 * default — in the latter case the legacy flat mirror seeds it instead.
 */
function isDefaultPlotSettings(settings: LayoutPlotSettings): boolean {
  const d = defaultPlotSettings();
  return (
    settings.pageSetupName === d.pageSetupName &&
    settings.paperName === d.paperName &&
    settings.paperWidthMm === d.paperWidthMm &&
    settings.paperHeightMm === d.paperHeightMm &&
    settings.units === d.units &&
    settings.margins.left === d.margins.left &&
    settings.margins.bottom === d.margins.bottom &&
    settings.margins.right === d.margins.right &&
    settings.margins.top === d.margins.top &&
    settings.plotAreaType === d.plotAreaType &&
    settings.window.lowerLeftX === d.window.lowerLeftX &&
    settings.window.lowerLeftY === d.window.lowerLeftY &&
    settings.window.upperRightX === d.window.upperRightX &&
    settings.window.upperRightY === d.window.upperRightY &&
    settings.scale.numerator === d.scale.numerator &&
    settings.scale.denominator === d.scale.denominator &&
    settings.offset.x === d.offset.x &&
    settings.offset.y === d.offset.y &&
    settings.rotation === d.rotation &&
    settings.plotStyleName === d.plotStyleName &&
    settings.flags === d.flags
  );
}

/**
 * Compute the effective {@link LayoutPlotSettings} to serialise for a model
 * Layout. `plotSettings` is the AUTHORITATIVE per-layout plot configuration
 * (CLAUDE.md PRIORITY #1 — plotSettings = source of truth) and is serialised
 * verbatim whenever it carries any non-default value.
 *
 * The four legacy flat fields (paperSize/plotPaperUnits/plotRotation/
 * plotOrigin) are NOT an override layer — a DEFAULT flat value must never
 * clobber an authoritative `plotSettings` (Codex P2 #189: a mm / rotated page
 * stored only in `plotSettings`, with the flat fields holding their schema
 * defaults `plotPaperUnits=0`/inches + `plotRotation=0`, was exported as
 * inches / no-rotation — corruption of the very thing PDF-6.1 preserves).
 *
 * Instead the flat mirror only SEEDS `plotSettings` when the latter is still
 * the canonical schema default — i.e. the layout was built from ONLY the flat
 * shape (legacy code paths / hand-built `dxfBridge.test.ts` fixtures, before
 * `plotSettings` existed). In that case the flat fields are the legacy
 * authority and are folded in so the intended units/rotation/paper/offset
 * still round-trip instead of silently reverting to A4. Re-imported layouts
 * always carry a flat mirror in sync with `plotSettings` (convertRawLayout),
 * so this branch is reached only by programmatic / fixture layouts.
 */
export function effectivePlotSettings(layout: Layout): LayoutPlotSettings {
  const base = layout.plotSettings ?? defaultPlotSettings();
  const out: LayoutPlotSettings = {
    ...base,
    margins: { ...base.margins },
    window: { ...base.window },
    scale: { ...base.scale },
    offset: { ...base.offset },
  };
  // `plotSettings` is authoritative when it differs from the canonical
  // default — return it untouched so a DEFAULT flat mirror cannot override it.
  if (!isDefaultPlotSettings(out)) return out;
  // `plotSettings` is the schema default → the flat mirror is the legacy
  // authority for a flat-only layout. Seed the four mirrored fields from it.
  if (layout.paperSize) out.paperName = layout.paperSize;
  if (typeof layout.plotPaperUnits === "number") {
    out.units = toPlotPaperUnits(layout.plotPaperUnits);
  }
  if (typeof layout.plotRotation === "number") {
    out.rotation = toPlotRotation(layout.plotRotation);
  }
  if (layout.plotOrigin) {
    out.offset = { x: layout.plotOrigin.x, y: layout.plotOrigin.y };
  }
  return out;
}

export function exportLayouts(
  layouts: Record<string, Layout> | undefined,
): DxfLayoutEntry[] {
  if (!layouts) return [];
  const out: DxfLayoutEntry[] = [];
  for (const layout of Object.values(layouts)) {
    const entry: DxfLayoutEntry = {
      kind: "layout",
      handle: layout.handle,
      name: layout.name,
      tabOrder: layout.tabOrder,
      minimumLimits: layout.minimumLimits,
      maximumLimits: layout.maximumLimits,
      insertionBase: layout.insertionBase,
      minimumExtents: layout.minimumExtents,
      maximumExtents: layout.maximumExtents,
      elevation: layout.elevation,
      orthographicViewType: layout.orthographicViewType,
    };
    // The full PLOTSETTINGS sub-object is the source of truth on the model;
    // the flat Layout fields act as an override layer for legacy callers
    // (see effectivePlotSettings).
    applyPlotSettingsToEntry(entry, effectivePlotSettings(layout));
    if (layout.ownerBlockRecordHandle) {
      entry.ownerHandle = layout.ownerBlockRecordHandle;
    }
    // ROOT C — the ACAD_LAYOUT dictionary owner (the 330 in the
    // AcDbPlotSettings-leading context). The writer emits the AcDbLayout 330
    // (BLOCK_RECORD back-ref) ONLY when this dictionary owner is also present,
    // so a multi-tab document pairs each LAYOUT↔BLOCK_RECORD correctly.
    if (layout.dictionaryOwnerHandle) {
      entry.dictionaryOwnerHandle = layout.dictionaryOwnerHandle;
    }
    // round-17 BUG 3 — re-emit the HEADER-slot `102` reactor / xdictionary block
    // in its original positional slot (the writer `serializeLayoutEntry` emits
    // `headerExtraGroups` between the 5/330 owner and the first `100` subclass
    // marker). Copy the model-local `{ code, value }[]` back into a `DxfGroup[]`
    // (plain array copy — structurally identical). Omitted when absent so the
    // round-trip is identical for layouts without a header-slot 102 block.
    if (layout.headerExtraGroups && layout.headerExtraGroups.length > 0) {
      entry.headerExtraGroups = layout.headerExtraGroups.map((g) => ({
        code: g.code,
        value: g.value,
      }));
    }
    out.push(entry);
  }
  return out;
}

// NOTE: a future `linkBlockRecordsToLayouts` helper will propagate the
// LAYOUT → BLOCK_RECORD handle link once BlockRecord grows a `handle`
// field. Today the forward direction (BLOCK_RECORD.layoutHandle →
// LAYOUT.handle) already round-trips via the TABLES section parser in
// dxfBridge.blockRecords.ts; the reverse link from LAYOUT is stored on
// `layout.ownerBlockRecordHandle` and emitted verbatim by the writer.
